import { Scroll, Sheet } from "@silk-hq/components";
import { cn } from "@trotroways/utils";
import {
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

type SheetState = "peek" | "expanded";

interface BottomSheetProps {
  children: ReactNode;
  peekHeight?: number;
  className?: string;
  state?: SheetState;
  onStateChange?: (state: SheetState) => void;
}

const DEFAULT_PEEK_HEIGHT = 400;

// Bottom inset (px) currently covered by the bottom sheet, or null when the
// sheet isn't in the DOM (desktop) — used so the map can fit routes in the
// visible area above the sheet instead of behind it.
export function getBottomSheetBottomInset(): number | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLElement>("[data-trotro-bottom-sheet]");
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.height <= 0) return null;
  return typeof window !== "undefined" ? window.innerHeight - rect.top : null;
}

function useIsMobileViewport(): boolean {
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = () => {
      setIsMobileViewport(mediaQuery.matches);
    };

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isMobileViewport;
}

export const BottomSheet = memo(function BottomSheet({
  children,
  peekHeight = DEFAULT_PEEK_HEIGHT,
  className,
  state: controlledState,
  onStateChange,
}: BottomSheetProps) {
  const isMobileViewport = useIsMobileViewport();
  const [internalState, setInternalState] = useState<SheetState>("peek");

  const state = controlledState ?? internalState;
  const activeDetent = state === "expanded" ? 2 : 1;
  const detents = useMemo(() => [`${peekHeight}px`], [peekHeight]);

  const setState = useCallback(
    (next: SheetState) => {
      if (onStateChange) {
        onStateChange(next);
      } else {
        setInternalState(next);
      }
    },
    [onStateChange],
  );

  const handleHandleClick = useCallback(() => {
    setState(state === "peek" ? "expanded" : "peek");
  }, [state, setState]);

  const handleActiveDetentChange = useCallback(
    (detent: number) => {
      setState(detent >= 2 ? "expanded" : "peek");
    },
    [setState],
  );

  if (!isMobileViewport) {
    return null;
  }

  return (
    <Sheet.Root
      license="commercial"
      defaultPresented
      defaultActiveDetent={1}
      activeDetent={activeDetent}
      onActiveDetentChange={handleActiveDetentChange}
    >
      <Sheet.Portal>
        <Sheet.View
          contentPlacement="bottom"
          tracks="bottom"
          detents={detents}
          inertOutside={false}
          swipeDismissal={false}
          onClickOutside={{ dismiss: false }}
          onEscapeKeyDown={{ dismiss: false }}
          nativeEdgeSwipePrevention
          className="pointer-events-none h-(--silk-100-lvh-dvh-pct) w-full"
        >
          <Sheet.Content
            data-trotro-bottom-sheet
            className={cn(
              "pointer-events-auto z-30 flex w-full flex-col overflow-hidden",
              "rounded-t-2xl ",
              "backdrop-blur-[50px]",
              "shadow-[0_-4px_24px_rgba(0,0,0,0.08)]",
              "max-h-[85vh]",
              className,
            )}
          >
            <Sheet.BleedingBackground className="rounded-t-2xl bg-[#0009] " />

            <button
              type="button"
              onClick={handleHandleClick}
              className="flex w-full items-center justify-center py-2.5 outline-none"
              aria-label={state === "peek" ? "Expand panel" : "Collapse panel"}
            >
              <div className="h-1 w-8 rounded-full bg-zinc-300" />
            </button>

            <Scroll.Root className="min-h-0 flex-1">
              <Scroll.View
                className="h-full"
                nativeScrollbar={false}
                nativeFocusScrollPrevention
                scrollGestureTrap={true}
              >
                <Scroll.Content
                  className={cn(
                    "px-3 pb-6",
                    state === "expanded" ? "overflow-y-auto" : "overflow-y-hidden",
                  )}
                >
                  {children}
                </Scroll.Content>
              </Scroll.View>
            </Scroll.Root>
          </Sheet.Content>
        </Sheet.View>
      </Sheet.Portal>
    </Sheet.Root>
  );
});
